import { type PlatformBusClass } from '~/apiClient';

type UseSlpFilterLabelsReturn = {
    getFeatureLabel: (feature: BooleanRouteParamsKey) => string;
    getEnumLabel: (key: EnumMultiRouteParamsKey, value: string) => string;
    getPlatformClassLabel: (platformClass: PlatformBusClass) => string;
    getCountryLabel: (countryCode: string) => string;
    formatRangeValue: (key: NumericFromToRouteParamsKey, value: number) => string;
    getRangeLabel: (key: NumericFromToRouteParamsKey, bound: 'from' | 'to', value: number) => string;
    getQueryLabel: (q: string) => string;
};

/** How the SLP writes the value of a filter, in its options and in its chips: one rule for each kind of filter. */
export const useSlpFilterLabels = (): UseSlpFilterLabelsReturn => {
    // COMPOSABLES
    const { tt, tte, tn, formatUnits } = useTypedI18n();
    const { getLocalizedCountry } = useCountries();

    // DATA
    const rangeFormats: Readonly<Record<NumericFromToRouteParamsKey, (value: number) => string>> = {
        companionSeats: String,
        displacement: value => formatUnits.cc(value)!,
        firstRegistrationYear: String,
        foldingSeats: String,
        fuelTankCapacity: value => formatUnits.liters(value)!,
        height: value => formatUnits.meters(value)!,
        km: value => formatUnits.km(value)!,
        length: value => formatUnits.meters(value)!,
        openableWindows: String,
        power: value => formatUnits.kw(value)!,
        price: value => tn(value, 'currency'),
        seats: String,
        standingPlaces: String,
        totalCapacity: String,
        wheelchairPlaces: String,
        width: value => formatUnits.meters(value)!,
    };

    // METHODS
    const formatRangeValue: UseSlpFilterLabelsReturn['formatRangeValue'] = (key, value) => rangeFormats[key](value);

    const getEnumLabel: UseSlpFilterLabelsReturn['getEnumLabel'] = (key, value) => {
        if (tte(`asset.${key}.${value}`)) return tt(`asset.${key}.${value}` as TranslationKey);
        if (tte(`slp.filters.${key}.${value}`)) return tt(`slp.filters.${key}.${value}` as TranslationKey);

        return value;
    };

    return {
        getFeatureLabel: feature => tt(`slp.filters.${feature}`),
        getEnumLabel,
        getPlatformClassLabel: platformClass => tt(`asset.class.${platformClass}`),
        getCountryLabel: countryCode => getLocalizedCountry(countryCode) ?? countryCode,
        formatRangeValue,
        getRangeLabel: (key, bound, value) =>
            tt(bound === 'from' ? 'slp.appliedFilters.from' : 'slp.appliedFilters.to', [
                tte(`slp.filters.${key}`) ? tt(`slp.filters.${key}` as TranslationKey) : '',
                formatRangeValue(key, value),
            ]),
        getQueryLabel: q => tt('slp.appliedFilters.q', [q]),
    };
};
